/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Bell, Check, CheckCircle2, X } from "lucide-react";
import { sosApi } from "./api";
import { toast } from "../ui";
import "./SosPanel.css";

const POLL_MS = 10_000;

function fmtAgo(iso) {
  if (!iso) return "";
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  } catch (_) { return ""; }
}

const STATUS_LABELS = {
  ACTIVE: "SOS raised — waiting for response",
  ACKNOWLEDGED: "Help is on the way",
  RESOLVED: "Incident resolved",
  CANCELLED: "Cancelled",
};

const STATUS_DOT_CLASS = {
  ACTIVE: "sos-status-dot--active",
  ACKNOWLEDGED: "sos-status-dot--acknowledged",
  RESOLVED: "sos-status-dot--resolved",
  CANCELLED: "sos-status-dot--cancelled",
};

const CARD_CLASS = {
  ACTIVE: "sos-active-card--active",
  ACKNOWLEDGED: "sos-active-card--acknowledged",
  RESOLVED: "sos-active-card--resolved",
  CANCELLED: "sos-active-card--cancelled",
};

const RESP_PILL_CLASS = {
  ACTIVE: "sos-resp-status-pill--active",
  ACKNOWLEDGED: "sos-resp-status-pill--acknowledged",
  RESOLVED: "sos-resp-status-pill--resolved",
  CANCELLED: "sos-resp-status-pill--cancelled",
};

// ── Resident raise panel ─────────────────────────────────────────────────────
function ResidentPanel() {
  const [activeSos, setActiveSos] = useState(null);
  const [raising, setRaising] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const list = await sosApi.list({});
      const events = Array.isArray(list) ? list : list.events || [];
      const active = events.find(e => e.status === "ACTIVE" || e.status === "ACKNOWLEDGED");
      setActiveSos(active || null);
    } catch (_) {}
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const raise = async () => {
    setRaising(true);
    try {
      const event = await sosApi.raise({});
      setActiveSos(event);
      toast.success("SOS raised — help is on the way");
    } catch (e) {
      if (e?.status === 409 || e?.detail?.includes?.("active")) {
        toast.error("You already have an active SOS");
        await load();
      } else {
        toast.error(e?.detail || e?.message || "Could not raise SOS");
      }
    } finally {
      setRaising(false);
    }
  };

  const cancel = async () => {
    if (!activeSos) return;
    setCancelling(true);
    try {
      await sosApi.cancel(activeSos.id);
      setActiveSos(null);
      toast.success("SOS cancelled");
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not cancel");
      await load();
    } finally {
      setCancelling(false);
    }
  };

  if (activeSos) {
    return (
      <div className={`sos-active-card ${CARD_CLASS[activeSos.status] || ""}`}>
        <div className="sos-active-top">
          <span className={`sos-status-dot ${STATUS_DOT_CLASS[activeSos.status] || ""}`} />
          <span className="sos-active-status-label">
            {STATUS_LABELS[activeSos.status] || activeSos.status}
          </span>
          <span className="sos-active-time">{fmtAgo(activeSos.created_at)}</span>
        </div>
        {activeSos.status === "RESOLVED" && activeSos.note && (
          <p className="sos-active-desc">Note: {activeSos.note}</p>
        )}
        {(activeSos.status === "ACTIVE" || activeSos.status === "ACKNOWLEDGED") && (
          <button className="sos-cancel-btn" disabled={cancelling} onClick={cancel}>
            {cancelling ? "Cancelling…" : "Cancel SOS"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="sos-raise-panel">
      <span className="sos-raise-icon">🆘</span>
      <div className="sos-raise-body">
        <p className="sos-raise-title">Emergency SOS</p>
        <p className="sos-raise-sub">Alert all security guards immediately</p>
      </div>
      <button className="sos-raise-btn" disabled={raising} onClick={raise}>
        {raising ? "Raising…" : "Raise SOS"}
      </button>
    </div>
  );
}

// ── Responder panel (admin / incharge) ───────────────────────────────────────
function ResponderPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [resolveId, setResolveId] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await sosApi.list({});
      setEvents(Array.isArray(data) ? data : data.events || []);
    } catch (_) {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const acknowledge = async (e) => {
    setBusy(e.id);
    try {
      await sosApi.acknowledge(e.id);
      toast.success("Acknowledged — marked as responding");
      await load();
    } catch (err) {
      toast.error(err?.detail || err?.message || "Could not acknowledge");
    } finally {
      setBusy(null);
    }
  };

  const startResolve = (e) => {
    setResolveId(e.id);
    setResolveNote("");
  };

  const submitResolve = async () => {
    setBusy(resolveId);
    try {
      await sosApi.resolve(resolveId, { note: resolveNote });
      toast.success("SOS resolved");
      setResolveId(null);
      await load();
    } catch (err) {
      toast.error(err?.detail || err?.message || "Could not resolve");
    } finally {
      setBusy(null);
    }
  };

  const active = events.filter(e => e.status === "ACTIVE" || e.status === "ACKNOWLEDGED");
  const past = events.filter(e => e.status === "RESOLVED" || e.status === "CANCELLED");

  if (loading) return <div className="sos-resp-empty">Loading…</div>;

  return (
    <div className="sos-resp-panel">
      {active.length === 0 && (
        <div className="sos-resp-empty">
          <CheckCircle2 size={24} style={{ marginBottom: 8 }} />
          <br />No active SOS events
        </div>
      )}

      {active.map(e => (
        <div key={e.id} className={`sos-resp-event sos-resp-event--${e.status.toLowerCase()}`}>
          <div className="sos-resp-event-top">
            <div className="sos-resp-event-meta">
              <strong>{e.raiser_name || e.resident_name || "Unknown"}</strong>
              <span className="sos-resp-event-sub">
                {e.villa_number ? `${e.villa_number}` : e.kind === "GUARD" ? "Guard SOS" : ""}
                {" · "}{fmtAgo(e.created_at)}
              </span>
            </div>
            <span className={`sos-resp-status-pill ${RESP_PILL_CLASS[e.status] || ""}`}>
              {e.status}
            </span>
          </div>

          {resolveId === e.id ? (
            <div>
              <input
                className="sos-note-input"
                placeholder="Resolution note (optional)…"
                value={resolveNote}
                onChange={ev => setResolveNote(ev.target.value)}
              />
              <div className="sos-resp-actions">
                <button className="sos-resp-btn" onClick={() => setResolveId(null)}>Cancel</button>
                <button
                  className="sos-resp-btn sos-resp-btn--resolve"
                  disabled={busy === e.id}
                  onClick={submitResolve}
                >
                  {busy === e.id ? "Resolving…" : "Confirm resolve"}
                </button>
              </div>
            </div>
          ) : (
            <div className="sos-resp-actions">
              {e.status === "ACTIVE" && (
                <button
                  className="sos-resp-btn sos-resp-btn--ack"
                  disabled={busy === e.id}
                  onClick={() => acknowledge(e)}
                >
                  {busy === e.id ? "…" : "Acknowledge"}
                </button>
              )}
              <button
                className="sos-resp-btn sos-resp-btn--resolve"
                disabled={busy === e.id}
                onClick={() => startResolve(e)}
              >
                Resolve
              </button>
            </div>
          )}
        </div>
      ))}

      {past.length > 0 && (
        <>
          <div className="sos-resp-past-label">Recent history</div>
          {past.slice(0, 10).map(e => (
            <div key={e.id} className="sos-resp-event sos-resp-event--past">
              <div className="sos-resp-event-top">
                <div className="sos-resp-event-meta">
                  <strong>{e.raiser_name || e.resident_name || "Unknown"}</strong>
                  <span className="sos-resp-event-sub">
                    {e.villa_number ? `${e.villa_number}` : ""}
                    {" · "}{fmtAgo(e.created_at)}
                    {e.note ? ` · ${e.note}` : ""}
                  </span>
                </div>
                <span className={`sos-resp-status-pill ${RESP_PILL_CLASS[e.status] || ""}`}>
                  {e.status}
                </span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Entry point ───────────────────────────────────────────────────────────────
export default function SosPanel({ mode = "resident" }) {
  if (mode === "responder") return <ResponderPanel />;
  return <ResidentPanel />;
}
